import {
   Typography,
   List,
   ListItem,
   ListItemIcon,
   ListItemText,
   Box,
   Container,
   Paper,
   Button,
   Alert,
   Badge,
   Divider,
   CircularProgress,
} from '@mui/material';
import { Person as PersonIcon } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { get_subscription_plan_pricing } from '../../services';

const PersonalAccountSubscription = () => {
   const navigate = useNavigate();
   const [searchParams] = useSearchParams();
   const [error, setError] = useState<string | null>(null);
   const [planPricing, setPlanPricing] = useState<any>(null);
   const [loadingPricing, setLoadingPricing] = useState(true);

   const { currentUser } = useUser();
   const { subscriptions, refreshSubscriptions, loading: subscriptionsLoading } = useSubscription();

   // Fetch subscription plan pricing from database
   useEffect(() => {
      const fetchPlanPricing = async () => {
         try {
            setLoadingPricing(true);
            // Pass user email to get subscription period calculations from Stripe
            console.log('About to call API with email:', currentUser?.email);
            const pricing = await get_subscription_plan_pricing(currentUser?.email || undefined);
            console.log('Fetched plan pricing:', pricing);
            console.log('Current user email:', currentUser?.email);
            console.log('Pricing response keys:', Object.keys(pricing || {}));
            setPlanPricing(pricing);
         } catch (error) {
            console.error('Error fetching plan pricing:', error);
            setError('Failed to load subscription pricing');
         } finally {
            setLoadingPricing(false);
         }
      };

      if (currentUser) {
         fetchPlanPricing();
      }
   }, [currentUser]);

   // Check for cancellation after returning from Stripe portal
   useEffect(() => {
      const fromPortal = searchParams.get('from');
      if (fromPortal === 'portal' && subscriptions && subscriptions.length > 0) {
         const currentSubscription = subscriptions[0];

         // Check if subscription was canceled or set to cancel at period end
         const wasCanceled =
            currentSubscription.status === 'canceled' ||
            currentSubscription.cancel_at_period_end === true;

         if (wasCanceled) {
            // Redirect to cancel confirmation page
            navigate('/cancel-confirmation', { replace: true });
            return;
         }

         // Clean up URL parameter if no cancellation detected
         navigate('/subscription', { replace: true });
      }
   }, [searchParams, subscriptions, navigate]);

   // Helper function to format date
   const formatDate = (dateValue: any) => {
      if (!dateValue) return 'N/A';

      // Handle Firestore Timestamp
      if (dateValue && typeof dateValue.toDate === 'function') {
         return dateValue.toDate().toLocaleDateString();
      }

      // Handle ISO string
      if (typeof dateValue === 'string') {
         return new Date(dateValue).toLocaleDateString();
      }

      // Handle Date object
      if (dateValue instanceof Date) {
         return dateValue.toLocaleDateString();
      }

      return 'N/A';
   };

   // Helper function to calculate end year
   const getEndYear = (startedAt: any, interval: string) => {
      if (!startedAt) return 'N/A';

      let startDate;
      if (startedAt && typeof startedAt.toDate === 'function') {
         startDate = startedAt.toDate();
      } else if (typeof startedAt === 'string') {
         startDate = new Date(startedAt);
      } else if (startedAt instanceof Date) {
         startDate = startedAt;
      } else {
         return 'N/A';
      }

      const endDate = new Date(startDate);
      if (interval === 'year') {
         endDate.setFullYear(endDate.getFullYear() + 1);
      } else if (interval === 'month') {
         endDate.setMonth(endDate.getMonth() + 1);
      }

      return endDate.getFullYear();
   };

   // Helper function to get plan cost from fetched pricing data
   const getPlanCost = (plan: string, interval: string) => {
      if (!planPricing || loadingPricing) {
         return loadingPricing ? 'Loading...' : 'N/A';
      }

      const planData = planPricing[plan];
      if (!planData) {
         console.warn(`No pricing data found for plan: ${plan}`);
         return 'N/A';
      }

      const { amount, currency = 'usd' } = planData;
      const currencySymbol = currency === 'usd' ? '$' : currency.toUpperCase();

      return `${currencySymbol}${amount}/${interval === 'year' ? 'year' : 'month'}`;
   };

   const handleManageSubscription = async (subscriptionId: string) => {
      try {
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/manage-subscription`, {
            method: 'POST',
            body: JSON.stringify({
               subscriptionId,
               userId: currentUser?.uid,
            }),
            headers: {
               'Content-Type': 'application/json',
            },
         });

         const data = await response.json();

         if (data.success) {
            // Redirect to Stripe Customer Portal
            window.location.href = data.url;
         } else {
            setError(data.message || 'Failed to open subscription management');
         }
      } catch (error) {
         setError(error instanceof Error ? error.message : 'An unknown error occurred');
      }
   };

   const handleResumeSubscription = (subscriptionPlan: string) => {
      // Redirect to subscription checkout to resume canceled subscription
      navigate(
         `/public-subscription?userId=${currentUser?.uid}&email=${currentUser?.email}&plan=${subscriptionPlan}&resume=true`
      );
   };

   // Show loading state while subscriptions are being fetched
   if (subscriptionsLoading || loadingPricing) {
      return (
         <Container maxWidth='lg' sx={{ mt: 5 }}>
            <Paper
               sx={{
                  p: 3,
                  mt: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 200,
               }}
            >
               <CircularProgress size={40} sx={{ mb: 2 }} />
               <Typography variant='h6' color='text.secondary'>
                  Loading subscription details...
               </Typography>
            </Paper>
         </Container>
      );
   }

   return (
      <Container maxWidth='lg' sx={{ mt: 5 }}>
         {error && (
            <Alert sx={{ mb: 2 }} severity='error'>
               {error}
            </Alert>
         )}
         <Paper
            sx={{
               p: 3,
               mt: 3,
               mb: 4,
            }}
         >
            <Box
               sx={{
                  position: 'relative',
                  zIndex: 2,
                  justifyContent: 'space-between',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 3,
               }}
            >
               <Box>
                  <Typography variant='h4' component='h1'>
                     Subscription Management
                  </Typography>
                  <Typography variant='body1'>
                     Manage your subscription and billing information.
                  </Typography>
               </Box>
            </Box>
         </Paper>

         {/* list all user suscription */}
         <Box sx={{ width: '100%' }}>
            <Typography variant='h6' sx={{ mb: 2 }}>
               All Subscriptions
            </Typography>

            {/* Show subscriptions if available */}
            {subscriptions && subscriptions.length > 0 ? (
               <List
                  sx={{
                     width: '100%',
                     mb: 2,
                     display: 'flex',
                     alignItems: 'flex-start',
                     flexWrap: 'wrap',
                     gap: 2,
                  }}
               >
                  {subscriptions?.map((subscription: any) => {
                     console.log('=== SUBSCRIPTION DEBUG ===');
                     console.log('Rendering subscription:', subscription);
                     console.log('Subscription plan:', subscription.plan);
                     console.log('Available plan pricing keys:', Object.keys(planPricing || {}));
                     console.log(
                        'Plan pricing for this subscription:',
                        planPricing?.[subscription.plan]
                     );
                     console.log('Full planPricing object:', planPricing);
                     console.log('=== END DEBUG ===');
                     return (
                        <ListItem
                           key={subscription.subscriptionId}
                           sx={{
                              maxWidth: 320,
                              mb: 2,
                              p: 0,
                              background: (theme) =>
                                 `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.primary.main}03 100%)`,
                              border: '1px solid',
                              borderColor: 'primary.main',
                              borderRadius: 2,
                              boxShadow: (theme) => `0 4px 16px ${theme.palette.primary.main}15`,
                              overflow: 'hidden',
                              transition: 'all 0.3s ease-in-out',
                              '&:hover': {
                                 transform: 'translateY(-2px)',
                                 boxShadow: (theme) => `0 6px 24px ${theme.palette.primary.main}25`,
                                 borderColor: 'primary.dark',
                              },
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'stretch',
                           }}
                        >
                           {/* Header with gradient */}
                           <Box
                              sx={{
                                 background: (theme) =>
                                    `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                 p: 2,
                                 position: 'relative',
                                 borderTopLeftRadius: 2,
                                 borderTopRightRadius: 2,
                              }}
                           >
                              <Box
                                 sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                 }}
                              >
                                 <Box>
                                    <Typography
                                       variant='subtitle1'
                                       sx={{
                                          color: 'white',
                                          fontWeight: 600,
                                          fontSize: '1rem',
                                          mb: 0.2,
                                          letterSpacing: '-0.2px',
                                       }}
                                    >
                                       {subscription?.plan?.toLowerCase() ===
                                       process.env.REACT_APP_STANDARD_PERSONAL_YEARLY_LOOKUP_KEY?.toLowerCase()
                                          ? 'Personal Account'
                                          : 'Organization Account'}
                                    </Typography>
                                    <Typography
                                       sx={{
                                          color: 'rgba(255, 255, 255, 0.7)',
                                          fontSize: '0.75rem',
                                          fontWeight: 400,
                                       }}
                                    >
                                       Subscription Plan
                                    </Typography>
                                 </Box>
                                 <Badge
                                    sx={{
                                       '& .MuiBadge-badge': {
                                          backgroundColor: (() => {
                                             if (subscription.cancel_at_period_end) {
                                                return '#ff9800'; // Orange for canceling
                                             }
                                             return subscription.status === 'active'
                                                ? '#4caf50' // Green for active
                                                : '#f44336'; // Red for inactive
                                          })(),
                                          color: 'white',
                                          fontWeight: 500,
                                          fontSize: '0.65rem',
                                          textTransform: 'uppercase',
                                          letterSpacing: '0.5px',
                                          px: 1,
                                          py: 0.2,
                                          borderRadius: '12px',
                                          boxShadow: '0 1px 6px rgba(0,0,0,0.15)',
                                          minWidth: '50px',
                                          height: '20px',
                                       },
                                    }}
                                    color={
                                       subscription.cancel_at_period_end
                                          ? 'warning'
                                          : subscription.status === 'active'
                                            ? 'success'
                                            : 'error'
                                    }
                                    badgeContent={
                                       subscription.cancel_at_period_end
                                          ? 'CANCELING'
                                          : subscription.status.toUpperCase()
                                    }
                                 />
                              </Box>
                           </Box>

                           {/* Content Area */}
                           <Box
                              sx={{
                                 p: 2,
                                 display: 'flex',
                                 flexDirection: 'column',
                                 gap: 1,
                                 background: 'rgba(255, 255, 255, 0.8)',
                                 backdropFilter: 'blur(10px)',
                              }}
                           >
                              <Typography
                                 sx={{
                                    color: 'text.primary',
                                    fontWeight: 500,
                                    fontSize: '0.85rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.8,
                                 }}
                              >
                                 <Box
                                    sx={{
                                       width: 6,
                                       height: 6,
                                       borderRadius: '50%',
                                       backgroundColor: 'primary.main',
                                       boxShadow: (theme) =>
                                          `0 0 0 1px ${theme.palette.primary.main}33`,
                                    }}
                                 />
                                 Billing Cycle: {subscription.interval}ly
                              </Typography>
                              <Typography color='text.secondary' fontWeight='bold'>
                                 Subscription Start:{' '}
                                 {(() => {
                                    console.log(
                                       'Checking subscription start for plan:',
                                       subscription.plan
                                    );
                                    console.log('Plan pricing exists:', !!planPricing);
                                    console.log(
                                       'Plan data exists:',
                                       !!planPricing?.[subscription.plan]
                                    );
                                    console.log(
                                       'Subscription period exists:',
                                       !!planPricing?.[subscription.plan]?.subscriptionPeriod
                                    );

                                    if (
                                       planPricing &&
                                       planPricing[subscription.plan]?.subscriptionPeriod
                                    ) {
                                       return planPricing[subscription.plan].subscriptionPeriod
                                          .startDateFormatted;
                                    }
                                    return formatDate(subscription.started_at);
                                 })()}
                              </Typography>
                              <Typography color='text.secondary' fontWeight='bold'>
                                 End Year:{' '}
                                 {(() => {
                                    if (
                                       planPricing &&
                                       planPricing[subscription.plan]?.subscriptionPeriod
                                    ) {
                                       return new Date(
                                          planPricing[subscription.plan].subscriptionPeriod.endDate
                                       ).getFullYear();
                                    }
                                    return getEndYear(
                                       subscription.started_at,
                                       subscription.interval
                                    );
                                 })()}
                              </Typography>
                              <Typography color='text.secondary' fontWeight='bold'>
                                 Cost:{' '}
                                 {(() => {
                                    console.log('Getting cost for plan:', subscription.plan);
                                    if (planPricing && planPricing[subscription.plan]) {
                                       const planData = planPricing[subscription.plan];
                                       const currencySymbol =
                                          planData.currency === 'usd'
                                             ? '$'
                                             : planData.currency.toUpperCase();
                                       return `${currencySymbol}${planData.amount}/${planData.interval}`;
                                    }
                                    return getPlanCost(subscription.plan, subscription.interval);
                                 })()}
                              </Typography>
                              {planPricing &&
                                 planPricing[subscription.plan]?.subscriptionPeriod && (
                                    <>
                                       <Typography color='text.secondary' fontWeight='bold'>
                                          End Date:{' '}
                                          {
                                             planPricing[subscription.plan].subscriptionPeriod
                                                .endDateFormatted
                                          }
                                       </Typography>
                                       <Typography color='text.secondary' fontWeight='bold'>
                                          Days Remaining:{' '}
                                          {
                                             planPricing[subscription.plan].subscriptionPeriod
                                                .daysRemaining
                                          }{' '}
                                          days
                                       </Typography>
                                    </>
                                 )}
                           </Box>

                           {/* Action Area */}
                           <Box
                              sx={{
                                 p: 2,
                                 pt: 1.5,
                                 background: 'rgba(248, 250, 252, 0.8)',
                                 borderTop: (theme) => `1px solid ${theme.palette.primary.main}14`,
                                 display: 'flex',
                                 gap: 1.5,
                              }}
                           >
                              <Button
                                 onClick={() => {
                                    if (subscription.status === 'canceled') {
                                       handleResumeSubscription(subscription.plan);
                                    } else {
                                       handleManageSubscription(subscription.subscriptionId);
                                    }
                                 }}
                                 variant='contained'
                                 size='small'
                                 sx={{
                                    flex: 1,
                                    background: (theme) =>
                                       subscription.status === 'canceled' ||
                                       subscription.cancel_at_period_end
                                          ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                                          : `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark} 100%)`,
                                    color: 'white',
                                    fontWeight: 500,
                                    py: 0.5,
                                    px: 1,
                                    fontSize: '0.75rem',
                                    textTransform: 'none',
                                    borderRadius: 1,
                                    boxShadow: (theme) =>
                                       subscription.status === 'canceled' ||
                                       subscription.cancel_at_period_end
                                          ? `0 1px 4px ${theme.palette.primary.main}30`
                                          : `0 1px 4px ${theme.palette.secondary.main}30`,
                                    transition: 'all 0.3s ease-in-out',
                                    '&:hover': {
                                       background: (theme) =>
                                          subscription.status === 'canceled' ||
                                          subscription.cancel_at_period_end
                                             ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.dark} 100%)`
                                             : `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                                       transform: 'translateY(-1px)',
                                       boxShadow: (theme) =>
                                          subscription.status === 'canceled' ||
                                          subscription.cancel_at_period_end
                                             ? `0 2px 6px ${theme.palette.primary.main}40`
                                             : `0 2px 6px ${theme.palette.secondary.main}40`,
                                    },
                                    '&:active': {
                                       transform: 'translateY(0)',
                                    },
                                 }}
                              >
                                 {subscription.status === 'canceled' ||
                                 subscription.cancel_at_period_end
                                    ? 'Resume Subscription'
                                    : 'Manage Subscription'}
                              </Button>
                           </Box>
                        </ListItem>
                     );
                  })}
               </List>
            ) : (
               <Box
                  sx={{
                     display: 'flex',
                     flexDirection: 'column',
                     alignItems: 'center',
                     width: '100%',
                     height: '100%',
                     justifyContent: 'center',
                     mt: 5,
                  }}
               >
                  <Typography variant='h6' sx={{ mb: 2 }}>
                     No subscriptions found
                  </Typography>

                  <Button
                     onClick={() =>
                        navigate(
                           `/public-subscription?userId=${currentUser?.uid}&email=${currentUser?.email}`
                        )
                     }
                     variant='contained'
                     color='primary'
                     sx={{ mt: 2 }}
                  >
                     Get a subscription plan
                  </Button>
               </Box>
            )}
         </Box>
      </Container>
   );
};

export default PersonalAccountSubscription;
